class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.searchAbortController = null;
    this.activeQueryKey = '';

    const headerSearch = [].slice.call(document.querySelectorAll('.header__search'));
    const activeSearch = headerSearch.find((search) => window.getComputedStyle(search).display !== 'none');

    if (activeSearch) this.searchModal = activeSearch.querySelector('.search-modal');

    if (this.input && this.predictiveSearchResults) this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this.searchAbortController) this.searchAbortController.abort();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener(
      'input',
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      if (this.searchAbortController) this.searchAbortController.abort();
      this.activeQueryKey = '';
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (!allElements.length || (moveUp && !selectedElement)) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (!activeElement || activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    this.setLiveRegionText(activeElement.textContent || '');
    if (activeElement.id) this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.activeQueryKey = queryKey;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.searchAbortController) this.searchAbortController.abort();
    this.searchAbortController = new AbortController();

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][fields]=title,product_type,variants.sku,variants.title,vendor&section_id=predictive-search`;

    fetch(url, { signal: this.searchAbortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search request failed with ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (this.activeQueryKey !== queryKey) return;

        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const section = documentFragment.querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search response did not contain the expected section');

        const resultsMarkup = section.innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        if (this.activeQueryKey === queryKey) {
          this.removeAttribute('loading');
          this.close();
        }
        console.error(error);
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    window.setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(count ? count.textContent : '');
  }

  getHeaderHeight() {
    this.headerHeight = this.searchModal ? `${Math.round(this.searchModal.offsetHeight)}px` : '0px';
    return this.headerHeight;
  }

  open() {
    const searchHeaderHeight = this.headerHeight || this.getHeaderHeight();
    document.documentElement.style.setProperty('--search-header-height', searchHeaderHeight);
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    if (this.input) {
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
    }

    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    this.isOpen = false;
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
